import * as React from 'react'
import { cn } from '@/lib/utils'

/**
 * Fluent 2 Card Component
 * https://fluent2.microsoft.design/components/web/react/core/card/usage
 *
 * Features:
 * - Border radius: 12px (rounded-xl)
 * - Shadow: Fluent 2 elevation tokens
 * - Motion: Uses Fluent 2 easing curves for transitions
 */

const Card = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & {
    interactive?: boolean
    elevation?: 'subtle' | 'medium' | 'high'
  }
>(({ className, interactive, elevation = 'subtle', ...props }, ref) => {
  const elevationClasses = {
    subtle: 'shadow-[0_2px_4px_rgba(0,0,0,0.04),0_0_2px_rgba(0,0,0,0.06)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.2),0_0_2px_rgba(0,0,0,0.15)]',
    medium: 'shadow-[0_2px_4px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)] dark:shadow-[0_2px_4px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]',
    high: 'shadow-[0_4px_8px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)] dark:shadow-[0_4px_8px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]',
  }

  return (
    <div
      ref={ref}
      className={cn(
        'rounded-xl border border-[#e5e5e5] bg-white dark:border-[#3d3d3d] dark:bg-[#292929]',
        elevationClasses[elevation],
        interactive && [
          'cursor-pointer',
          'transition-all duration-200 ease-[cubic-bezier(0.33,0,0.67,1)]',
          'hover:shadow-[0_4px_8px_rgba(0,0,0,0.14),0_0_2px_rgba(0,0,0,0.12)]',
          'dark:hover:shadow-[0_4px_8px_rgba(0,0,0,0.3),0_0_2px_rgba(0,0,0,0.2)]',
          'hover:border-[#d4d4d4] dark:hover:border-[#525252]',
          'active:scale-[0.99]',
        ],
        className
      )}
      {...props}
    />
  )
})
Card.displayName = 'Card'

const CardHeader = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex flex-col space-y-1 p-4', className)}
    {...props}
  />
))
CardHeader.displayName = 'CardHeader'

const CardTitle = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h3
    ref={ref}
    className={cn(
      // Fluent 2 Subtitle 2 typography
      'text-base font-semibold leading-[22px] tracking-[-0.01em] text-[#1a1a1a] dark:text-white',
      className
    )}
    {...props}
  />
))
CardTitle.displayName = 'CardTitle'

const CardDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <p
    ref={ref}
    className={cn(
      // Fluent 2 Body 2 typography
      'text-sm leading-5 text-[#616161] dark:text-[#adadad]',
      className
    )}
    {...props}
  />
))
CardDescription.displayName = 'CardDescription'

const CardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('px-4 pb-4', className)} {...props} />
))
CardContent.displayName = 'CardContent'

const CardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn('flex items-center gap-2 px-4 pb-4', className)}
    {...props}
  />
))
CardFooter.displayName = 'CardFooter'

export { Card, CardHeader, CardFooter, CardTitle, CardDescription, CardContent }
